import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api, inr } from "../lib/api";
import ProductCard from "../components/ProductCard";
import BrandFilter from "../components/BrandFilter";
import FitFilter from "../components/FitFilter";
import PriceRange from "../components/PriceRange";
import SpecFilters from "../components/SpecFilters";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { CATS as ALL, SUBS } from "../lib/categories";
import { t } from "../lib/i18n";
import { useSeo } from "../lib/seo";
const CATS = [["", "All"], ...ALL.map((c) => [c.slug, c.name])];

function Filters({ sp, set, setSp }) {
  const chip = (active) => `px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? "bg-[#ff5500] border-[#ff5500] text-white" : "border-white/10 text-slate-300 hover:border-white/40"}`;
  return (
    <aside className="space-y-6" data-testid="filters-panel">
      <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{t("Category")}</p>
        <div className="flex flex-wrap gap-2">{CATS.map(([v, n]) => <button key={v} data-testid={`filter-category-${v || "all"}`} onClick={() => set("category", v)} className={chip((sp.get("category") || "") === v)}>{t(n)}</button>)}</div></div>
      <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{t("Price")}</p>
        <PriceRange sp={sp} set={set} /></div>
      <SpecFilters category={sp.get("category") || ""} sp={sp} setSp={setSp} />
      <FitFilter sp={sp} set={set} />
      <BrandFilter sp={sp} set={set} />
      <div><p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Customer rating</p>
        <div className="flex flex-wrap gap-2">{[["", "Any"], ["4", "4★ & up"], ["4.5", "4.5★ & up"]].map(([v, n]) => <button key={v} data-testid={`filter-rating-${v || "any"}`} onClick={() => set("rating", v)} className={chip((sp.get("rating") || "") === v)}>{n}</button>)}</div></div>
    </aside>
  );
}

export default function Products() {
  const { cat: pcat } = useParams();
  const nav = useNavigate();
  const [raw] = useSearchParams();
  const sp = useMemo(() => { const n = new URLSearchParams(raw); if (pcat) n.set("category", pcat); return n; }, [raw, pcat]);
  const setSp = (v) => { const n = new URLSearchParams(v); const c = n.get("category"); n.delete("category"); const s = n.toString(); nav(c ? `/category/${c}${s ? `?${s}` : ""}` : `/products${s ? `?${s}` : ""}`); };
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);

  const set = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); if (k === "category") { n.delete("sub"); n.delete("brands"); } setSp(n); };
  const cat = sp.get("category") || "";
  const sub = sp.get("sub") || "";

  useEffect(() => {
    const [min, max] = (sp.get("price") || "").split("-");
    const params = { limit: 200, ids: sp.get("ids") || "", q: sp.get("q") || "", category: sp.get("category") || "", sub: sp.get("sub") || "", brands: sp.get("brands") || "", sort: sp.get("sort") || "relevance", min_rating: sp.get("rating") || 0 };
    if (min) Object.assign(params, { min_price: min, max_price: max });
    const [fw, fh, fd] = (sp.get("fit") || "").split("x");
    if (fd) Object.assign(params, { fit_w: fw, fit_h: fh, fit_d: fd, unknown: sp.get("unknown") || "show" });
    setItems(null);
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v != null));
    sp.getAll("spec").forEach((x) => qs.append("spec", x));
    api.get(`/products?${qs}`).then((r) => { setItems(r.data); setTotal(+r.headers["x-total-count"] || r.data.length); });
  }, [sp]);

  const q = sp.get("q");
  const catName = CATS.find(([v]) => v === cat)?.[1];
  const subs = SUBS[cat] || [];
  useSeo(cat && !q ? `${sub || catName}: Buy ${sub ? `${catName} ${sub}` : catName} Online at Best Prices | EAZYSHOPY` : q ? `Search results for "${q}" | EAZYSHOPY Online Shopping` : null,
    cat && !q ? `Shop ${sub || catName} online on EAZYSHOPY${subs.length ? ` – ${subs.join(", ")}` : ""}. Best prices, fast delivery across India, cash on delivery and easy returns.` : null);
  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-12 py-8" data-testid="products-page">
      <div className="flex flex-wrap items-end gap-4 mb-8">
        <div>
          <p className="text-sm font-semibold text-[#ff5500]" data-testid="products-total-count">{items ? `${Math.max(total, items.length).toLocaleString("en-IN")} products${items.length < total ? ` · showing ${items.length}` : ""}` : "Loading..."}</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{sp.get("title") || (q ? `"${q}"` : sub || CATS.find(([v]) => v === cat)?.[1] || "All") + (sub || sp.get("title") ? "" : " products")}</h1>
          {sub && <p className="text-sm text-slate-500" data-testid="products-breadcrumb">{CATS.find(([v]) => v === cat)?.[1]} › {sub}</p>}
        </div>
        <div className="ml-auto flex gap-2">
          <button data-testid="filters-toggle" onClick={() => setOpen((o) => !o)} className={`${sp.get("ids") ? "hidden" : "lg:hidden flex"} items-center gap-2 px-4 h-10 rounded-full border border-white/10`}><SlidersHorizontal size={16} />{t("Filters")}</button>
          <Select value={sp.get("sort") || "relevance"} onValueChange={(v) => set("sort", v === "relevance" ? "" : v)}>
            <SelectTrigger data-testid="sort-select" className="w-48 rounded-full h-10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">Popularity</SelectItem>
              <SelectItem value="price_asc">Price: Low to High</SelectItem>
              <SelectItem value="price_desc">Price: High to Low</SelectItem>
              <SelectItem value="bestseller">Bestsellers</SelectItem>
              <SelectItem value="rating">Customer Rating</SelectItem>
              <SelectItem value="newest">Newest First</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {cat && SUBS[cat] && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-8 -mt-2" data-testid="subcategory-chips">
          {["", ...SUBS[cat]].map((s) => (
            <button key={s || "all"} data-testid={`subcat-chip-${s ? s.toLowerCase().replace(/[^a-z0-9]+/g, "-") : "all"}`} onClick={() => set("sub", s)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${sub === s ? "bg-white text-black border-white" : "border-white/15 text-slate-300 hover:border-white/50"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      )}
      <div className={`grid gap-8 ${sp.get("ids") ? "" : "lg:grid-cols-[260px_minmax(0,1fr)]"}`}>
        <div className={sp.get("ids") ? "hidden" : `${open ? "block" : "hidden"} lg:block lg:sticky lg:top-32 lg:self-start lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto no-scrollbar`}>
          <Filters sp={sp} set={set} setSp={setSp} />
          {[...sp.keys()].length > 0 && <button data-testid="filters-clear" onClick={() => setSp({})} className="mt-6 flex items-center gap-1 text-sm text-[#ff5500]"><X size={14} />Clear all</button>}
        </div>
        <div>
          {items && items.length === 0 && <p data-testid="products-empty" className="text-slate-400 py-20 text-center">No products match your filters.</p>}
          <div className={`grid grid-cols-2 gap-3 sm:gap-4 lg:gap-5 ${sp.get("ids") ? "lg:grid-cols-4" : "md:grid-cols-3 2xl:grid-cols-4"}`}>
            {(items || []).map((p, i) => (
              <Fragment key={p.id}>
                {p.fit === "unknown" && items[i - 1]?.fit !== "unknown" && <p data-testid="size-unknown-divider" className="col-span-full text-xs uppercase tracking-wider text-slate-500 pt-4 border-t border-white/10">Size unknown — check dimensions before buying</p>}
                <ProductCard p={p} i={i} />
              </Fragment>
            ))}
          </div>
          {!q && !sp.get("ids") && (catName || !cat) && (
            <section className="mt-16 max-w-3xl text-sm text-slate-400 leading-relaxed space-y-3" data-testid="category-about">
              <h2 className="text-base md:text-lg font-bold text-slate-200">About {sub ? `${sub} in ${catName}` : cat ? catName : "online shopping"} on EAZYSHOPY</h2>
              <p>Buy {sub || cat ? (sub || catName).toLowerCase() : "mobiles, electronics, fashion, groceries and more"} online at EAZYSHOPY from trusted, verified sellers across India. Every product page shows clear prices, MRP and discount, real customer reviews and multi-view photos so you can see each item from the front, back, sides and top before you order.</p>
              {!cat && <p>Our catalogue spans {CATS.filter(([v]) => v).map(([, n]) => n).join(", ")}. Pick a category above to see its sections, or search by product name, brand or model number in English or Telugu.</p>}
              {items?.length > 0 && <p>Popular picks right now include {items.slice(0, 5).map((p) => p.title).join(", ")}, with prices starting from {inr(Math.min(...items.map((p) => p.price)))}.</p>}
              {cat && !sub && subs.length > 0 && <p>Popular {catName.toLowerCase()} sections include {subs.join(", ")}. Use the filters to narrow results by price, brand, rating and specifications, or sort by popularity, newest arrivals and best sellers.</p>}
              <p>Enter your PIN code on any product to check delivery to your area. Pay with cash on delivery, track your order live and return eligible items easily. Our support team is available by chat, ticket and phone whenever you need help.</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
